"use client";

import { getOrgDashboardApiV1AdminOrgDashboardGet } from "@/src/api/sdk.gen";
import { useEffect, useState } from "react";
import { Link } from "@/src/lib/next-compat";
import { ArrowRight, Cpu, BarChart3, Terminal, Sparkles } from "lucide-react";

interface CourseData {
    id: number;
    uuid: string;
    title: string;
    enrolled: number;
    completed: number;
}

interface DashboardData {
    totalEnrolled: number;
    courses: CourseData[];
}

export default function AdminDashboardClient() {
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getOrgDashboardApiV1AdminOrgDashboardGet()
            .then(({ data: resData, error }) => {
                if (error || !resData) {
                    throw new Error("Failed to load org dashboard");
                }
                setData(resData as unknown as DashboardData);
            })
            .catch((err) => console.error("Failed to fetch dashboard:", err))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="h-56 bg-slate-100 rounded-2xl animate-pulse" />
                ))}
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center py-12 text-slate-500">
                Failed to load dashboard data.
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {data.courses.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-slate-500">
                        No courses found.
                    </div>
                ) : (
                    data.courses.map((course) => {
                        let CourseIcon = Cpu;
                        if (course.title.includes("Data")) CourseIcon = BarChart3;
                        else if (course.title.includes("Python")) CourseIcon = Terminal;
                        else if (course.title.includes("GenAI")) CourseIcon = Sparkles;

                        return (
                            <Link
                                key={course.id}
                                to={`/admin/dashboard/courses/${course.uuid}/progress`}
                                className="block group relative"
                            >
                                <div className="bg-indigo-600 rounded-2xl p-5 text-white relative overflow-hidden transition-all hover:shadow-xl hover:shadow-indigo-500/20 hover:-translate-y-1 h-full">
                                    <div className="absolute top-0 right-0 -mr-16 -mt-16 w-56 h-56 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-all" />

                                    <div className="absolute top-4 right-4 text-indigo-400/30">
                                        <CourseIcon className="h-20 w-20 transform rotate-12" />
                                    </div>

                                    <div className="relative z-10 flex flex-col h-full">
                                        <div className="mb-6 min-h-[3.5rem] flex flex-col items-center text-center px-4">
                                            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mb-3 backdrop-blur-sm border border-white/10 shadow-inner">
                                                <CourseIcon className="h-5 w-5 text-white" />
                                            </div>
                                            <h3 className="text-lg font-medium line-clamp-2 uppercase tracking-widest font-montserrat">
                                                {course.title}
                                            </h3>
                                        </div>

                                        <div className="mt-auto pt-3 border-t border-indigo-500/30 space-y-2">
                                            <div className="flex items-center justify-between text-xs text-indigo-200">
                                                {course.enrolled === 0 ? (
                                                    <span>No students yet</span>
                                                ) : (
                                                    <>
                                                        <span>{course.enrolled} started</span>
                                                        <span>{course.completed} completed</span>
                                                    </>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between text-xs font-medium hover:text-indigo-300 transition-colors">
                                                View Details
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })
                )}
        </div>
    );
}
